import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, UncontrolledTooltip } from 'reactstrap';
import withRouter from '../.././components/withRouter';
import TableContainer from '../.././components/TableContainerReactTable';
import AddEditPublisherModal from './AddEditPublisherModal';
import { getPublishers } from '../../api/publisherApi';
import { Publisher } from '../../types/publisher';
import ImportPublisherModal from './ImportPublisherModal';
import { useProfile } from '../../hooks/UserHooks';

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<Publisher[]>([]);
    const [dataToEdit, setDataToEdit] = useState<Publisher | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddEditPublisherModalOpen, setIsAddOrderModalOpen] = useState<boolean>(false);
    const [isImportPublisherModalOpen, setIsImportPublisherModalOpen] = useState<boolean>(false);

    useEffect(() => {
        // Lista os publishers cadastrados na tabela
        getPublishers().then((res) => {
            setData(res);
            setLoading(false);
        });
    }, []);

    const handleUpdateData = (newData: Publisher) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: Publisher | Publisher[]) => {
        if (Array.isArray(newData)) {
            setData([...data, ...newData]);
        } else {
            setData([...data, newData]);
        }
    };

    const toggleAddEditPublisherModal = () => {
        setIsAddOrderModalOpen(!isAddEditPublisherModalOpen);
    };

    const toggleImportPublisherModal = () => {
        setIsImportPublisherModalOpen(!isImportPublisherModalOpen);
    }

    const columns = [
        {
            header: 'Mídia Cad',
            cell: (cell: any) => {
                return (
                    <span className="fw-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'mediaCad',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Ad Network',
            accessorFn: (row: any) => {
                return row.adNetwork?.name ?? '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Site / App',
            accessorFn: (row: any) => {
                return row.site || row.app;
            },
            cell: (cell: any) => {
                const row = cell.row.original;
                if (row.site) {
                    return (<span>{row.site} <a href={'https://' + row.site} target="_blank" rel="noreferrer"><i className="ri-external-link-line"></i></a></span>);
                }

                if (row.app) {
                    return row.app;
                }

                return '-';
            },
            enableColumnFilter: false,
        },
        {
            header: 'TAGs',
            accessorFn: (row: any) => {
                return row.tags?.length || 0;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Campanhas',
            accessorFn: (row: any) => {
                const campaignsCountSet = new Set();
                if (row.orders && row.orders.length > 0) {
                    row.orders.forEach((order: any) => {
                        campaignsCountSet.add(order.campaign.id);
                    });
                }
                return campaignsCountSet.size;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Blocos de anúncios',
            accessorFn: (row: any) => {
                return row.placements?.length || 0;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Usuários',
            accessorFn: (row: any) => {
                return row.users?.length || 0;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                // Midia Kit
                if (row.mediaKitUrl || row.mediaKitFile) {
                    actionCell.push(<>
                        <a href={row.mediaKitUrl || row.mediaKitFile}
                           id={'action_midia_kit_' + row.id}
                           rel={'noreferrer'}
                           target="_blank"
                           className="btn-icon material-shadow-none btn btn-primary"
                        >
                            <i className="ri-image-line"></i>
                        </a>
                        <UncontrolledTooltip
                            placement="top"
                            target={'action_midia_kit_' + row.id}
                        >
                            Mídia Kit
                        </UncontrolledTooltip>
                    </>);
                }

                // Botão de editar
                if (userProfile?.role === 'ROLE_SUPER_ADMIN' || userProfile?.publisher?.id === row.id) {
                    actionCell.push(<>
                        <button className="btn-icon material-shadow-none btn btn-success"
                                id={'action_edit_' + row.id}
                                onClick={() => {
                                    setDataToEdit(row);
                                    toggleAddEditPublisherModal();
                                }}
                        ><i className="ri-pencil-fill fs-16"></i></button>
                        <UncontrolledTooltip
                            placement="top"
                            target={'action_edit_' + row.id}
                        >
                            Editar
                        </UncontrolledTooltip>
                    </>);
                }
                return <div className="d-flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
        },
    ];

    document.title = 'Publishers | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <Row className="g-4 align-items-center">
                                        <div className="col-sm">
                                            <div>
                                                <h5 className="card-title mb-0">Publishers</h5>
                                            </div>
                                        </div>
                                        <div className="col-sm-auto">
                                            <button
                                                type="button"
                                                className="btn btn-success add-btn me-1"
                                                id="create-btn"
                                                onClick={() => {
                                                    setDataToEdit(null);
                                                    toggleAddEditPublisherModal();
                                                }}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i> Adicionar publisher
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-success add-btn me-1"
                                                id="create-btn"
                                                onClick={toggleImportPublisherModal}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i> Importar publishers
                                            </button>
                                        </div>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    {!loading ? <TableContainer
                                                divClass="table-responsive"
                                                columns={(columns || [])}
                                                data={(data || [])}
                                                isGlobalFilter={true}
                                                customPageSize={30}
                                                SearchPlaceholder="Procurar..."
                                            /> :
                                        <div className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Carregando...</span>
                                            </div>
                                        </div>}

                                    <AddEditPublisherModal
                                        publisher={dataToEdit}
                                        isOpen={isAddEditPublisherModalOpen}
                                        toggle={toggleAddEditPublisherModal}
                                        onAddData={handleAddData}
                                        onUpdateData={handleUpdateData}
                                    />

                                    <ImportPublisherModal
                                        isOpen={isImportPublisherModalOpen}
                                        toggle={toggleImportPublisherModal}
                                        onAddData={handleAddData}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);